import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { MultiPhotoUploader } from '../components/MultiPhotoUploader';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { useAuth } from '../context/AuthContext';
import { useLocation as useUserLocation } from '../context/LocationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';
import {
  PawPrint,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  CheckCircle2,
  Info,
  ExternalLink
} from 'lucide-react';
import { Switch } from '../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIES_OPTIONS = [
  { id: 'dog',          label: 'Dog'           },
  { id: 'cat',          label: 'Cat'           },
  { id: 'bird',         label: 'Bird'          },
  { id: 'small_animal', label: 'Small Animal'  },
  { id: 'fish',         label: 'Fish / Aquatic' },
  { id: 'reptile',      label: 'Reptile'       },
  { id: 'other',        label: 'Other'         },
];

const AGE_OPTIONS = [
  { value: 1,   label: '< 3 months' },
  { value: 3,   label: '3–6 months' },
  { value: 6,   label: '6–12 months' },
  { value: 12,  label: '1–2 years' },
  { value: 24,  label: '2–5 years' },
  { value: 60,  label: '5–10 years' },
  { value: 120, label: '10+ years' },
];

const LISTING_TYPES = [
  {
    id: 'private_rehoming',
    label: 'Private Rehoming',
    sublabel: 'Free listing — up to 2 per year',
    description: 'You own this pet and are finding it a new home.',
    priceCap: 150,
  },
  {
    id: 'registered_breeder',
    label: 'Registered Breeder',
    sublabel: 'Paid listing (coming soon)',
    description: 'You are a licensed breeder selling animals.',
    priceCap: null,
  },
  {
    id: 'rescue_org',
    label: 'Rescue Organisation',
    sublabel: 'Free listing',
    description: 'You are an approved rescue or shelter.',
    priceCap: null,
  },
];

// Detect AU state from location string
function detectState(locationName) {
  const states = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
  const upper = (locationName || '').toUpperCase();
  return states.find(s => upper.includes(s)) || null;
}

// Get state ID field config based on state + species
function getStateIdConfig(state, species) {
  if (!['dog', 'cat'].includes(species)) return null;
  if (state === 'NSW') {
    return {
      label: 'Breeder ID Number (BIN) or RON',
      placeholder: 'e.g. BIN123456',
      help: 'Required by NSW law (Dec 2025). Find yours at the NSW Pet Registry.',
      link: 'https://www.petregistry.olg.nsw.gov.au',
      linkLabel: 'NSW Pet Registry →',
    };
  }
  if (state === 'VIC') {
    return {
      label: 'Pet Exchange Register (PER) Source Number',
      placeholder: 'e.g. RC123456',
      help: 'Required by Victorian law for all dog & cat listings.',
      link: 'https://agriculture.vic.gov.au/livestock-and-animals/animal-welfare-victoria/domestic-animals-act/pet-exchange-register',
      linkLabel: 'VIC Pet Exchange Register →',
    };
  }
  if (state === 'QLD' && species === 'dog') {
    return {
      label: 'QDBR Supply Number',
      placeholder: 'e.g. QLD123456',
      help: 'Required by Queensland law for all dog listings.',
      link: 'https://qdbr.dpi.qld.gov.au',
      linkLabel: 'QLD Dog Breeder Register →',
    };
  }
  return null;
}

const STEP_LABELS = ['Animal Details', 'Compliance & Health', 'Listing Details'];

export default function AddPetListingPage() {
  const { user, api } = useAuth();
  const { location: userLocation } = useUserLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    // Step 1 — Animal Details
    species: '',
    breed: '',
    age_months: '',
    sex: 'unknown',
    de_sexed: null,
    colour: '',
    images: [],
    primary_image_index: 0,

    // Step 2 — Compliance & Health
    microchip_number: '',
    state_id_number: '',
    vaccination_status: 'unknown',
    vet_checked: false,

    // Step 3 — Listing Details
    listing_type: 'private_rehoming',
    title: '',
    description: '',
    price: '0',
    location_name: userLocation?.name || '',
    location: userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : null,
    pets_terms_accepted: false,
  });

  const detectedState = detectState(form.location_name);
  const stateIdConfig = getStateIdConfig(detectedState, form.species);
  const currentListingType = LISTING_TYPES.find(t => t.id === form.listing_type);

  // Auto-suggest title when species/breed/sex/age changes
  useEffect(() => {
    if (!form.species) return;
    const speciesLabel = SPECIES_OPTIONS.find(s => s.id === form.species)?.label || form.species;
    const parts = [
      form.breed || speciesLabel,
      form.sex !== 'unknown' ? (form.sex === 'male' ? 'male' : 'female') : '',
      form.age_months ? (form.age_months >= 12 ? `${Math.round(form.age_months / 12)}yr` : `${form.age_months}mo`) : '',
      form.listing_type === 'private_rehoming' ? 'for rehoming' : 'for sale',
    ].filter(Boolean);
    const suggested = parts.join(' ');
    setForm(f => ({ ...f, title: suggested.charAt(0).toUpperCase() + suggested.slice(1) }));
  }, [form.species, form.breed, form.sex, form.age_months, form.listing_type]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  // Step validation
  function canAdvanceStep() {
    if (step === 0) return !!form.species;
    if (step === 1) {
      if (['dog', 'cat'].includes(form.species) && !form.microchip_number.trim()) return false;
      if (stateIdConfig && !form.state_id_number.trim()) return false;
      return true;
    }
    return true;
  }

  async function handleSubmit() {
    if (!form.pets_terms_accepted) {
      toast.error('Please accept the Pets Terms & Conditions');
      return;
    }
    if (!form.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!form.location) {
      toast.error('Please set a location');
      return;
    }

    const price = parseFloat(form.price) || 0;

    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || `${form.species} for ${form.listing_type === 'private_rehoming' ? 'rehoming' : 'sale'}`,
      category: 'live_animals',
      subcategory: form.species === 'dog' ? 'dogs'
        : form.species === 'cat' ? 'cats'
        : form.species === 'bird' ? 'birds'
        : form.species === 'small_animal' ? 'small_animals'
        : form.species === 'fish' ? 'fish_aquatic'
        : form.species === 'reptile' ? 'reptiles'
        : 'animals_other',
      estimated_value: price,
      price: price,
      condition: 'good',
      is_free: price === 0,
      is_firm: false,
      is_public: true,
      location: form.location,
      location_name: form.location_name,
      images: form.images,
      primary_image_index: form.primary_image_index,
      tags: [],
      pet_details: {
        species: form.species,
        breed: form.breed || null,
        age_months: form.age_months ? parseInt(form.age_months) : null,
        sex: form.sex,
        de_sexed: form.de_sexed,
        colour: form.colour || null,
        microchip_number: form.microchip_number || null,
        state_id_type: stateIdConfig
          ? (detectedState === 'NSW' ? 'bin' : detectedState === 'VIC' ? 'per_source' : 'supply_number')
          : 'none',
        state_id_number: form.state_id_number || null,
        vaccination_status: form.vaccination_status,
        vet_checked: form.vet_checked,
        listing_type: form.listing_type,
        pets_terms_accepted: true,
      },
    };

    setSubmitting(true);
    try {
      const res = await api.post('/inventory/', payload);
      toast.success('Pet listed successfully!');
      navigate(`/pets/${res.data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to submit listing. Please check all required fields.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/pets" className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)]">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <PawPrint className="w-5 h-5 text-[#1E3A5F]" />
              <h1 className="text-xl font-bold text-[var(--theme-text-primary)]">List a Pet</h1>
            </div>
            <p className="text-sm text-[var(--theme-text-muted)]">Free for private rehomers · Welfare-first</p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {STEP_LABELS.map((label, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                i < step ? 'bg-[#059669] text-white'
                : i === step ? 'bg-[#1E3A5F] text-white'
                : 'bg-[var(--theme-hover)] text-[var(--theme-text-muted)]'
              }`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:block ${i === step ? 'text-[var(--theme-text-primary)] font-medium' : 'text-[var(--theme-text-muted)]'}`}>
                {label}
              </span>
              {i < STEP_LABELS.length - 1 && (
                <div className={`flex-1 h-px ${i < step ? 'bg-[#059669]' : 'bg-[var(--theme-border)]'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] p-6">

          {/* ── Step 0: Animal Details ── */}
          {step === 0 && (
            <div className="space-y-5">
              <h2 className="font-semibold text-[var(--theme-text-primary)]">Animal Details</h2>

              {/* Species */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-2">
                  Species <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {SPECIES_OPTIONS.map(s => (
                    <button
                      key={s.id}
                      onClick={() => set('species', s.id)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-lg border text-center transition-colors ${
                        form.species === s.id
                          ? 'border-[#1E3A5F] bg-[#1E3A5F]/10 text-[#1E3A5F]'
                          : 'border-[var(--theme-border)] hover:border-[#1E3A5F]/50 text-[var(--theme-text-secondary)]'
                      }`}
                    >
                      <PawPrint className="w-4 h-4" />
                      <span className="text-xs font-medium leading-tight">{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Breed */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Breed (optional)</label>
                <Input
                  value={form.breed}
                  onChange={e => set('breed', e.target.value)}
                  placeholder={form.species === 'dog' ? 'e.g. Labrador Retriever' : form.species === 'cat' ? 'e.g. Domestic Shorthair' : 'Breed or type'}
                  className="bg-[var(--theme-bg)] border-[var(--theme-border)]"
                />
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Age (optional)</label>
                <select
                  value={form.age_months}
                  onChange={e => set('age_months', e.target.value)}
                  className="w-full rounded-md border border-[var(--theme-border)] bg-[var(--theme-bg)] px-3 py-2 text-sm text-[var(--theme-text-primary)]"
                >
                  <option value="">Select age…</option>
                  {AGE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Sex + De-sexed */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Sex</label>
                  <div className="flex gap-2">
                    {['male', 'female', 'unknown'].map(s => (
                      <button
                        key={s}
                        onClick={() => set('sex', s)}
                        className={`flex-1 py-2 rounded-md text-xs font-medium border transition-colors ${
                          form.sex === s
                            ? 'border-[#1E3A5F] bg-[#1E3A5F]/10 text-[#1E3A5F]'
                            : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#1E3A5F]/50'
                        }`}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">De-sexed</label>
                  <div className="flex gap-2">
                    {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }, { v: null, l: 'Unknown' }].map(o => (
                      <button
                        key={String(o.v)}
                        onClick={() => set('de_sexed', o.v)}
                        className={`flex-1 py-2 rounded-md text-xs font-medium border transition-colors ${
                          form.de_sexed === o.v
                            ? 'border-[#1E3A5F] bg-[#1E3A5F]/10 text-[#1E3A5F]'
                            : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#1E3A5F]/50'
                        }`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Colour */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Colour / markings (optional)</label>
                <Input
                  value={form.colour}
                  onChange={e => set('colour', e.target.value)}
                  placeholder="e.g. Black and white, tabby, golden"
                  className="bg-[var(--theme-bg)] border-[var(--theme-border)]"
                />
              </div>

              {/* Photos */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-2">Photos (up to 6)</label>
                <MultiPhotoUploader
                  images={form.images}
                  primaryIndex={form.primary_image_index}
                  onImagesChange={imgs => set('images', imgs)}
                  onPrimaryChange={idx => set('primary_image_index', idx)}
                  maxImages={6}
                  api={null}
                />
              </div>
            </div>
          )}

          {/* ── Step 1: Compliance & Health ── */}
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-semibold text-[var(--theme-text-primary)]">Compliance & Health</h2>

              {/* Microchip — dogs/cats only */}
              {['dog', 'cat'].includes(form.species) && (
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Microchip Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={form.microchip_number}
                    onChange={e => set('microchip_number', e.target.value)}
                    placeholder="15-digit microchip number"
                    className="bg-[var(--theme-bg)] border-[var(--theme-border)]"
                  />
                  <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                    Required by law for all dogs and cats in Australia.
                  </p>
                </div>
              )}

              {/* Location (for state detection) */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  Your Location
                </label>
                <LocationAutocomplete
                  value={form.location_name}
                  onChange={(name) => set('location_name', name)}
                  onSelect={(loc) => {
                    set('location_name', loc.name);
                    set('location', { lat: loc.lat, lng: loc.lng });
                  }}
                  placeholder="Suburb or city"
                />
                {detectedState && (
                  <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                    Detected state: <strong>{detectedState}</strong>
                  </p>
                )}
              </div>

              {/* State ID field — conditional */}
              {stateIdConfig && (
                <div className="rounded-lg border border-[var(--theme-border)] bg-[var(--theme-card)] p-4" style={{ borderLeftWidth: '3px', borderLeftColor: '#D97706' }}>
                  <div className="flex items-start gap-2 mb-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#D97706' }} />
                    <p className="text-xs text-[var(--theme-text-secondary)]">
                      {stateIdConfig.help}
                    </p>
                  </div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    {stateIdConfig.label} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={form.state_id_number}
                    onChange={e => set('state_id_number', e.target.value)}
                    placeholder={stateIdConfig.placeholder}
                    className="bg-[var(--theme-bg)] border-[var(--theme-border)]"
                  />
                  <a
                    href={stateIdConfig.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#1E3A5F] hover:underline mt-2"
                  >
                    {stateIdConfig.linkLabel} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}

              {/* Vaccination status */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Vaccination Status</label>
                <div className="flex gap-2">
                  {[{ v: 'current', l: 'Current' }, { v: 'partial', l: 'Partial' }, { v: 'unknown', l: 'Unknown' }].map(o => (
                    <button
                      key={o.v}
                      onClick={() => set('vaccination_status', o.v)}
                      className={`flex-1 py-2 rounded-md text-xs font-medium border transition-colors ${
                        form.vaccination_status === o.v
                          ? 'border-[#1E3A5F] bg-[#1E3A5F]/10 text-[#1E3A5F]'
                          : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#1E3A5F]/50'
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Vet checked */}
              <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--theme-border)]">
                <div>
                  <p className="text-sm font-medium text-[var(--theme-text-primary)]">Vet checked</p>
                  <p className="text-xs text-[var(--theme-text-muted)]">Recently examined by a vet</p>
                </div>
                <Switch
                  checked={form.vet_checked}
                  onCheckedChange={(val) => set('vet_checked', val)}
                />
              </div>
            </div>
          )}

          {/* ── Step 2: Listing Details ── */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="font-semibold text-[var(--theme-text-primary)]">Listing Details</h2>

              {/* Listing type */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-2">Listing Type</label>
                <div className="space-y-2">
                  {LISTING_TYPES.map(lt => (
                    <button
                      key={lt.id}
                      onClick={() => set('listing_type', lt.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        form.listing_type === lt.id
                          ? 'border-[#1E3A5F] bg-[#1E3A5F]/5'
                          : 'border-[var(--theme-border)] hover:border-[#1E3A5F]/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[var(--theme-text-primary)]">{lt.label}</span>
                        <span className="text-xs text-[var(--theme-text-muted)]">{lt.sublabel}</span>
                      </div>
                      <p className="text-xs text-[var(--theme-text-muted)] mt-0.5">{lt.description}</p>
                    </button>
                  ))}
                </div>
                {form.listing_type === 'private_rehoming' && (
                  <div className="flex items-start gap-2 mt-2 p-3 rounded-lg bg-[var(--theme-hover)] border border-[var(--theme-border)]">
                    <Info className="w-4 h-4 text-[var(--theme-text-muted)] mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-[var(--theme-text-secondary)]">
                      Private rehoming listings are free. You may post up to 2 per year.
                      You may charge up to $150 to cover vet check costs.
                    </p>
                  </div>
                )}
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  {form.listing_type === 'private_rehoming' ? 'Rehoming Fee (optional, max $150)' : 'Asking Price'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--theme-text-muted)]">$</span>
                  <Input
                    type="number"
                    min="0"
                    max={form.listing_type === 'private_rehoming' ? 150 : undefined}
                    value={form.price}
                    onChange={e => set('price', e.target.value)}
                    className="pl-7 bg-[var(--theme-bg)] border-[var(--theme-border)]"
                    placeholder="0"
                  />
                </div>
                {form.listing_type === 'private_rehoming' && (
                  <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                    Set to $0 for free rehoming, or charge up to $150 to cover your vet check.
                  </p>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.title}
                  onChange={e => set('title', e.target.value)}
                  placeholder="e.g. Golden Retriever male 2yr for rehoming"
                  className="bg-[var(--theme-bg)] border-[var(--theme-border)]"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  rows={4}
                  placeholder="Tell potential adopters about the animal — temperament, history, ideal home, reason for rehoming…"
                  className="w-full rounded-md border border-[var(--theme-border)] bg-[var(--theme-bg)] px-3 py-2 text-sm text-[var(--theme-text-primary)] resize-none focus:outline-none focus:ring-1 focus:ring-[#1E3A5F]"
                />
              </div>

              {/* Pets T&Cs */}
              <div className={`p-4 rounded-lg border ${form.pets_terms_accepted ? 'border-[#059669] bg-[#059669]/5' : 'border-[var(--theme-border)]'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.pets_terms_accepted}
                    onChange={e => set('pets_terms_accepted', e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#1E3A5F]"
                  />
                  <span className="text-sm text-[var(--theme-text-secondary)]">
                    I have read and agree to the{' '}
                    <Link to="/pets/terms" target="_blank" className="text-[#1E3A5F] underline font-medium">
                      WANTD Pets Terms & Conditions
                    </Link>
                    . I confirm this listing complies with all applicable state animal welfare laws, the animal is microchipped (if required), and I will not ship the animal.{' '}
                    <span className="text-red-500">*</span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-[var(--theme-border)]">
            <Button
              variant="outline"
              onClick={() => step === 0 ? navigate('/pets') : setStep(s => s - 1)}
              disabled={submitting}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>

            {step < 2 ? (
              <Button
                onClick={() => setStep(s => s + 1)}
                disabled={!canAdvanceStep()}
                className="bg-[#1E3A5F] hover:bg-[#2d5a8e] text-white"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting || !form.pets_terms_accepted || !form.title.trim()}
                className="bg-[#059669] hover:bg-[#047857] text-white"
              >
                {submitting ? 'Submitting…' : 'List Pet'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
